import classes from "./files_versions.module.scss";
import { mediaManager } from "@common-reducers/MediaManagerReducer";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { IconButton, Button } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { BiDotsHorizontalRounded } from "react-icons/bi";
import React, { useState } from "react";
import FilesIcons from "@common-components/files-icons/filesIcons";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { getFilesCurrentIndexAndVersionCurrentIndex } from "@common-reducers/MediaManagerSelectors";
import { deleteVersion, setCurrentVersionAsPrimary } from "@common-reducers/MediaManagerThunks";

interface FileVersion {
    filename?: string;
    version?: number | string;
    updatedAt?: string;
    versions?: FileVersion[];
}

export default function FilesVersions() {
    const dispatch = useAppDispatch()
    const { fileList, filesCurrentIndex, currentFileVersion } = useAppSelector(getFilesCurrentIndexAndVersionCurrentIndex);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [versionMenu, setOpenVersionMenu] = useState<FileVersion | null>(null);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

    const currentFileShown: FileVersion | undefined = fileList?.[filesCurrentIndex];
    // The last element of `versions` mirrors the current file, so previous
    // versions are everything but the last entry.
    const previousVersions: FileVersion[] = currentFileShown?.versions?.slice(0, -1) ?? [];

    const openVersionMenu = (file: FileVersion) => (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
        setOpenVersionMenu(file);
    };
    const closeVersionMenu = (e?: React.MouseEvent | object) => {
        (e as React.MouseEvent)?.stopPropagation?.();
        setAnchorEl(null);
        setOpenVersionMenu(null);
    };
    function setVersionPreview(e: React.MouseEvent, i: number) {
        e.stopPropagation();
        dispatch(mediaManager.setVersionPreview({ isVersionPreview: i !== -1, versionIndex: i }));
    }
    function setAsCurrentVersion(e: React.MouseEvent, currentVersionFile: FileVersion, index: number) {
        e.stopPropagation();
        closeVersionMenu(e);
        dispatch(setCurrentVersionAsPrimary({ currentVersionFile, index }));
    }
    function confirmDeleteVersion(e: React.MouseEvent, index: number) {
        e.stopPropagation();
        closeVersionMenu(e);
        setPendingDeleteIndex(index);
    }
    function deleteSelectedVersion() {
        if (pendingDeleteIndex !== null) {
            dispatch(deleteVersion(pendingDeleteIndex));
        }
        setPendingDeleteIndex(null);
    }

    if (!currentFileShown) {
        return (
            <div className={classes.panel}>
                <div className={classes.panelHeader}>Versions</div>
                <div className={classes.emptyState}>No file selected</div>
            </div>
        );
    }

    return (
        <div className={classes.panel}>
            <div className={classes.panelHeader}>Versions</div>

            <div className={classes.sectionLabel}>Current version</div>
            <div
                onClick={(e) => setVersionPreview(e, -1)}
                className={`${classes.fileContainer} ${currentFileVersion === -1 ? classes.versionSelected : ''}`}
            >
                <FilesIcons file={currentFileShown} />
                <div className={classes.versionDetails}>
                    <div className={classes.fileNameContainer}>
                        <span className={classes.fileName} title={currentFileShown.filename}>{currentFileShown.filename}</span>
                        <span className={`${classes.versionBadge} ${classes.currentBadge}`}>V{currentFileShown.version}</span>
                    </div>
                    {currentFileShown.updatedAt && (
                        <span className={classes.timestamp}>Updated {currentFileShown.updatedAt}</span>
                    )}
                </div>
            </div>

            <div className={classes.sectionLabel}>Previous versions</div>
            {previousVersions.length === 0 ? (
                <div className={classes.emptyState}>No previous versions yet</div>
            ) : (
                <div className={classes.versionsColumn}>
                    {previousVersions.map((fileVersion: FileVersion, i: number) => (
                        <div
                            onClick={(e) => setVersionPreview(e, i)}
                            key={i}
                            className={`${classes.fileContainer} ${currentFileVersion === i ? classes.versionSelected : ''}`}
                        >
                            <FilesIcons file={fileVersion} />
                            <div className={classes.versionDetails}>
                                <div className={classes.fileNameContainer}>
                                    <span className={classes.fileName} title={currentFileShown.filename}>{currentFileShown.filename}</span>
                                    <span className={classes.versionMenu}>
                                        <IconButton
                                            style={{ padding: 2 }}
                                            aria-label="version menu"
                                            size="small"
                                            onClick={openVersionMenu(fileVersion)}
                                        >
                                            <BiDotsHorizontalRounded />
                                        </IconButton>
                                    </span>
                                    <Menu
                                        anchorEl={anchorEl}
                                        open={versionMenu === fileVersion}
                                        onClose={(e) => closeVersionMenu(e)}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    >
                                        <MenuItem onClick={(e) => setAsCurrentVersion(e, fileVersion, i)} sx={{ fontSize: 13 }}>Set as current version</MenuItem>
                                        <MenuItem onClick={(e) => confirmDeleteVersion(e, i)} sx={{ fontSize: 13, color: 'var(--color-danger)' }}>Delete version</MenuItem>
                                    </Menu>
                                </div>
                                <div className={classes.versionMetaRow}>
                                    <span className={classes.versionBadge}>V{fileVersion.version}</span>
                                    {fileVersion.updatedAt && <span className={classes.timestamp}>{fileVersion.updatedAt}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={pendingDeleteIndex !== null} onClose={() => setPendingDeleteIndex(null)}
                PaperProps={{ sx: { borderRadius: '12px', maxWidth: 360 } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Delete version?</DialogTitle>
                <DialogContent sx={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
                    This version will be permanently removed.
                </DialogContent>
                <DialogActions sx={{ padding: '12px 20px' }}>
                    <Button onClick={() => setPendingDeleteIndex(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={deleteSelectedVersion} color="error" variant="contained" disableElevation sx={{ textTransform: 'none' }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
